import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchBilling, fetchBillingStatus, canManageBilling, isPlatformAdmin } from '../api/billing';
import { fetchEmbedSettings } from '../api/embed';
import { deriveCardStatus, onBillingChange } from './billing/gateRules';

// The two gentle "!" nudges in the sidebar — billing not set up, and the
// fit-check embed not connected yet. Neither blocks anything (the CEO took
// the card gate out, 2026-11): they only point the brand at a page to finish.
//
// Both signals are read once per brand here and shared, so no page fetches
// them twice:
//   • billing  — GET /brands/:id/billing(/status) → cardStatus; "!" for
//                anything other than 'valid'. Unknown (403 / error / loading)
//                shows no badge.
//   • embed    — GET /brands/:id/embed/settings → status 'configured' (a valid
//                impression in the last 7 days, the brands-listed signal). "!"
//                while not configured. A Shopify brand reads the same.
//
// `connected` is what the Statisztikák empty state keys on, so it is null
// while unknown (never a false "set it up" flash before the answer is in).

const mockStatusParam = () => {
  try {
    return new URLSearchParams(window.location.search).get('embedMockStatus');
  } catch {
    return null;
  }
};

export default function usePortalBadges({ brand, user, embedded }) {
  const active = !embedded && !!brand && !!user;
  const brandId = brand?.id ?? null;
  const isAdmin = isPlatformAdmin(user);
  const canManage = !!brand && canManageBilling(brand, user?.id, isAdmin);
  const seq = useRef(0);
  const [state, setState] = useState({ brandId: null, cardStatus: undefined, connected: null });

  const check = useCallback(() => {
    if (!active) {
      setState({ brandId: null, cardStatus: undefined, connected: null });
      return Promise.resolve();
    }
    const mine = ++seq.current;
    // cardStatus: the owner / billing manager reads the full billing; everyone
    // else the status-only endpoint. The brand's own cardStatus (session) wins.
    const billing = brand.cardStatus
      ? Promise.resolve(brand.cardStatus)
      : (canManage ? fetchBilling(brandId) : fetchBillingStatus(brandId))
          .then((res) => deriveCardStatus(res.data ?? res))
          .catch(() => undefined); // 403 / error → no badge
    const embed = fetchEmbedSettings(brandId, { mockStatus: mockStatusParam() })
      .then((res) => (res?.data ?? res)?.status === 'configured')
      .catch(() => null); // error → unknown, no badge and no empty state

    return Promise.all([billing, embed]).then(([cardStatus, connected]) => {
      if (mine === seq.current) setState({ brandId, cardStatus, connected });
    });
  }, [active, brandId, canManage, brand?.cardStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    check();
  }, [check]);

  // The Számlázás page announces a card change; re-read so the "!" clears.
  useEffect(() => {
    if (!active) return undefined;
    return onBillingChange((e) => {
      if (!e.detail?.brandId || e.detail.brandId === brandId) check();
    });
  }, [active, brandId, check]);

  const current = state.brandId === brandId;
  const cardStatus = current ? state.cardStatus : undefined;
  const connected = current ? state.connected : null;

  return {
    cardBadge: active && cardStatus !== undefined && cardStatus !== 'valid',
    cardStatus,
    // Every brand runs the fit-check button on its webshop, Shopify or not,
    // so the "connect" nudge applies to all of them.
    connectBadge: active && connected === false,
    connected, // true | false | null (unknown)
    recheck: check,
  };
}
